import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, XCircle, Calendar, Search, TrendingUp } from "lucide-react";
import { listMyStudentAttendance } from "@/lib/api/parent-invites.functions";
import { LoadErrorState } from "@/components/parent/LoadErrorState";
import { describeLoadError } from "@/lib/browser-storage";

export const Route = createFileRoute("/_authenticated/responsavel/lista-chamada")({
  component: ParentAttendancePage,
});

function ParentAttendancePage() {
  const listFn = useServerFn(listMyStudentAttendance);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [search, setSearch] = useState("");
  const [studentFilter, setStudentFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listFn();
      setRecords(res.attendance ?? []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const students = useMemo(() => {
    const map = new Map<string, string>();
    records.forEach((r) => r.students?.id && map.set(r.students.id, r.students.name || "Aluno"));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [records]);

  const months = useMemo(() => {
    const set = new Set<string>();
    records.forEach((r) => {
      if (r.class_date) set.add(r.class_date.slice(0, 7));
    });
    return Array.from(set).sort().reverse();
  }, [records]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return records.filter((r) => {
      if (studentFilter !== "all" && r.students?.id !== studentFilter) return false;
      if (monthFilter !== "all" && !(r.class_date || "").startsWith(monthFilter)) return false;
      if (statusFilter === "present" && !r.present) return false;
      if (statusFilter === "absent" && r.present) return false;
      if (term) {
        const hay = `${r.students?.name || ""} ${r.class_date || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [records, search, studentFilter, monthFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const present = filtered.filter((r) => r.present).length;
    const absent = total - present;
    const pct = total ? Math.round((present / total) * 100) : 0;
    return { total, present, absent, pct };
  }, [filtered]);

  const groupedByMonth = useMemo(() => {
    const groups: Record<string, any[]> = {};
    filtered.forEach((r) => {
      const key = (r.class_date || "").slice(0, 7);
      (groups[key] ||= []).push(r);
    });
    return groups;
  }, [filtered]);

  function fmtMonth(ym: string) {
    if (!ym) return "";
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, (m || 1) - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  }

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;
  }

  if (error) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <LoadErrorState
          rawError={error}
          message={describeLoadError(error, "Erro ao carregar presenças.")}
          onRetry={() => void load()}
        />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Calendar className="w-6 h-6 text-primary" /> Lista de Chamada</h1>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Card><CardContent className="p-3 text-center">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-xl font-bold">{stats.total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <div className="text-xs text-muted-foreground">Presenças</div>
          <div className="text-xl font-bold text-emerald-600">{stats.present}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <div className="text-xs text-muted-foreground">Faltas</div>
          <div className="text-xl font-bold text-red-600">{stats.absent}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <div className="text-xs text-muted-foreground flex items-center justify-center gap-1"><TrendingUp className="w-3 h-3" /> Frequência</div>
          <div className="text-xl font-bold text-primary">{stats.pct}%</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar" className="pl-9" />
        </div>
        <Select value={studentFilter} onValueChange={setStudentFilter}>
          <SelectTrigger><SelectValue placeholder="Aluno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos alunos</SelectItem>
            {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger><SelectValue placeholder="Mês" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os meses</SelectItem>
            {months.map((m) => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="present">Somente presenças</SelectItem>
            <SelectItem value="absent">Somente faltas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Nenhum registro encontrado.
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByMonth).map(([month, items]) => (
          <div key={month} className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground capitalize">{fmtMonth(month)}</h3>
            {items.map((r) => {
              const ts = r.training_schedules;
              return (
                <Card key={r.id}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="font-medium text-sm">{r.students?.name || "Aluno"}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(r.class_date).toLocaleDateString("pt-BR")}
                        {ts && ` · ${ts.start_time?.slice(0, 5) || "--:--"}–${ts.end_time?.slice(0, 5) || "--:--"}`}
                        {ts?.name && ` · ${ts.name}`}
                      </div>
                      {r.notes && <div className="text-[11px] text-muted-foreground italic">{r.notes}</div>}
                      {r.created_at && (
                        <div className="text-[10px] text-muted-foreground">
                          Registrado em {new Date(r.created_at).toLocaleString("pt-BR")}
                        </div>
                      )}
                    </div>
                    <Badge className={r.present ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                      {r.present ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Presente</> : <><XCircle className="w-3 h-3 mr-1" /> Falta</>}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
